import { Maximize2, Minimize2, X } from "lucide-react";

/**
 * Props for the ToastHeader component
 *
 * @property {string} title - The title to display in the header
 * @property {string} description - Optional description to display below the title
 * @property {Function} onDismiss - Callback when the dismiss button is clicked (closes the toast)
 * @property {boolean} isExpanded - Whether the content is expanded
 * @property {Function} onToggleExpand - Callback when the expand/collapse button is clicked
 */
export type ToastHeaderProps = {
  title?: string;
  description?: string;
  onDismiss?: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

/**
 * Component that displays a header for toast notifications with expand/collapse functionality
 *
 * This component:
 * 1. Displays a title and optional description
 * 2. Shows an X button that calls onDismiss when clicked
 * 3. Shows an expand/collapse button that toggles the content visibility
 */
export const ToastHeader = ({
  title,
  description,
  onDismiss,
  isExpanded,
  onToggleExpand,
}: ToastHeaderProps) => {
  return (
    <div
      className="flex cursor-pointer items-center justify-between gap-6"
      onClick={onToggleExpand}
    >
      <div className="flex-1">
        <h1 className="text-lg font-semibold">{title}</h1>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="text-gray-500 hover:text-gray-700"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <Minimize2 className="size-4" />
          ) : (
            <Maximize2 className="size-4" />
          )}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <X className="size-5" />
        </button>
      </div>
    </div>
  );
};
